import { ApiClient } from '@api/api-configuration';
import { Doctor } from '@api/READ_ONLY/doctors_api/Api';
import { GenericSearchSelect } from '@app/components/ui/GenericSearchSelect';
import { SelectProps } from 'antd';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { type ApiFetchFunction, type ApiQueryParams } from '@app/components/ui/GenericSearchSelect';

type DoctorSelectProps = {
  onDoctorChange?: (doctor: Doctor | Doctor[] | undefined) => void;
  placeholder?: string;
  pageSize?: number;
  minSearchLength?: number;
  searchDebounceMs?: number;
  reloadSignal?: number | string;
  lazyLoadOnOpen?: boolean;
  selectedItemsProvider?: () => Doctor | Doctor[] | undefined;
  active?: boolean | null;
} & Omit<SelectProps, 'options' | 'onChange' | 'loading' | 'onSearch'>;

// Memoized option component for performance
const DoctorOption = memo(
  ({
    firstName,
    lastName,
    specialization,
  }: {
    firstName: string;
    lastName: string;
    specialization?: string | null;
  }) => {
    return (
      <div className="flex flex-col min-w-0 w-full">
        <span className="font-semibold">{`${firstName}, ${lastName}`}</span>
        {specialization && <span className="text-xs text-gray-500">{specialization}</span>}
      </div>
    );
  }
);
DoctorOption.displayName = 'DoctorOption';

export const DoctorSelectGeneric = ({
  onDoctorChange,
  placeholder,
  pageSize = 10,
  minSearchLength = 3,
  searchDebounceMs = 300,
  reloadSignal,
  lazyLoadOnOpen,
  selectedItemsProvider,
  active = true,
  ...selectProps
}: DoctorSelectProps) => {
  const { t } = useTranslation();
  const fetchDoctors: ApiFetchFunction<Doctor> = async (params: ApiQueryParams) => {
    const query = {
      ...params,
      is_active: active,
      sort: 'last_name',
    };
    return await ApiClient.doctorsApi.doctors.listDoctors(query);
  };

  return (
    <GenericSearchSelect<Doctor>
      {...selectProps}
      apiFetch={fetchDoctors}
      getItemId={(doctor) => doctor.doctorId}
      getItemLabel={(doctor) => `${doctor.firstName} ${doctor.lastName}`}
      onSelectionChange={onDoctorChange}
      placeholder={placeholder ?? t('doctors-select-placeholder')}
      pageSize={pageSize}
      minSearchLength={minSearchLength}
      searchDebounceMs={searchDebounceMs}
      searchPlaceholder={t('doctors-search-select-placeholder')}
      reloadSignal={reloadSignal}
      lazyLoadOnOpen={lazyLoadOnOpen}
      selectedItemsProvider={selectedItemsProvider}
      renderOption={(doctor) => (
        <DoctorOption firstName={doctor.firstName} lastName={doctor.lastName} specialization={doctor.specialization} />
      )}
    />
  );
};

export default DoctorSelectGeneric;
